import { getPaymentToken } from 'utilities/cookies'

/**
 * The app's sign-up WebView (mobile/src/components/client-sign-up-survey)
 * signs a guest in with their payment token just before it opens the native
 * paywall. Shipped app builds read that token from `paymentToken=` in the URL
 * of the page the survey navigates to. A token in the URL is readable by GTM
 * and the pixels on the page, so app builds that can receive it another way
 * say so before any page script runs:
 * `window.__kickoffAppCapabilities.paymentTokenMessage`. For those, the web
 * posts the token over the WebView bridge and leaves it out of the URL.
 */

export const PAYMENT_TOKEN_MESSAGE_TYPE = 'paymentToken'

/**
 * Not a credential: the id of the message posted for this navigation. The app
 * waits for the message with that id, and uses no other, rather than treating
 * a token-less URL as a guest.
 */
export const PAYMENT_HANDOFF_PARAM = 'paymentHandoff'

/** A correlation id, not a secret: it only pairs a message with its URL. */
const newHandoffId = () =>
  Date.now().toString(36) + Math.random().toString(36).slice(2, 10)

type AppWindow = Window & {
  ReactNativeWebView?: { postMessage: (message: string) => void }
  __kickoffAppCapabilities?: { paymentTokenMessage?: unknown }
}

/** Whether this page runs in an app build that takes the token by message. */
export const appReceivesPaymentTokenMessage = (
  win: AppWindow | undefined = typeof window === 'undefined'
    ? undefined
    : window
) =>
  !!win?.ReactNativeWebView &&
  !!win.__kickoffAppCapabilities?.paymentTokenMessage

/**
 * The query params that hand the guest's payment token to the app for the
 * navigation about to happen. Call it at navigation time, not while
 * rendering: for an app that takes the token by message, this posts it.
 * Older app builds get the token in the URL, as they always have.
 */
export const appPaymentTokenParams = (
  token: string | null | undefined = getPaymentToken(),
  win: AppWindow | undefined = typeof window === 'undefined'
    ? undefined
    : window
): Record<string, string> => {
  if (!token) return {}
  if (!appReceivesPaymentTokenMessage(win)) return { paymentToken: token }

  const id = newHandoffId()
  win!.ReactNativeWebView!.postMessage(
    JSON.stringify({ type: PAYMENT_TOKEN_MESSAGE_TYPE, token, id })
  )
  return { [PAYMENT_HANDOFF_PARAM]: id }
}
